import { Pressable, StyleSheet, View } from 'react-native';

import { memo, useMemo, type FC, type ReactNode } from 'react';

import { horizontalLtrDirectionStyle } from '../../../shared/i18n/rtl';
import { appTheme, useThemeColors } from '../../../shared/theme';
import { hexToRgba } from '../../../shared/theme/colorUtils';

import Animated, {
  Easing,
  useAnimatedStyle,
  useDerivedValue,
  withSpring,
  withTiming,
} from 'react-native-reanimated';

import type { SharedValue } from 'react-native-reanimated';

type PaginatorProps = {
  pagesAmount: number;
  currentPageIndex: SharedValue<number>;
  visibleDots: number;
  dotSize?: number;
  spacing?: number;
  background?: ReactNode;
  onDotPress?: (index: number) => void;
};

const SpringConfig = {
  damping: 15,
  stiffness: 100,
  mass: 0.5,
} as const;

export const Paginator: FC<PaginatorProps> = memo(
  ({
    pagesAmount,
    currentPageIndex,
    visibleDots,
    dotSize = 7,
    spacing = 8,
    onDotPress,
  }) => {
    const colors = useThemeColors() ?? appTheme.colors;
    const idle_dot = hexToRgba(colors.text, 0.28);
    const active_dot = colors.primary;
    const dots = useMemo(() => new Array(pagesAmount).fill(0), [pagesAmount]);

    const roundedPage = useDerivedValue(() => Math.round(currentPageIndex.value));

    const visibleDotsIndices = useDerivedValue(() => {
      const currentIndex = roundedPage.value;
      const halfVisible = Math.floor(visibleDots / 2);
      let start = Math.max(currentIndex - halfVisible, 0);
      const end = Math.min(start + visibleDots, pagesAmount);

      if (end === pagesAmount) {
        start = Math.max(pagesAmount - visibleDots, 0);
      }

      return { start, end, currentIndex };
    });

    const containerAnimatedStyle = useAnimatedStyle(() => {
      const { currentIndex } = visibleDotsIndices.value;
      const dotFullWidth = dotSize * 2.5;
      const regularDotWidth = dotSize;
      const totalSpacing = spacing * (pagesAmount - 1);
      const totalWidth = (pagesAmount - 1) * regularDotWidth + dotFullWidth;
      const centerOffset = (totalWidth + totalSpacing) / 2;
      const currentPosition = currentIndex * (regularDotWidth + spacing) + dotFullWidth / 2;
      const translateX = centerOffset - currentPosition;

      return {
        transform: [{ translateX: withSpring(translateX, SpringConfig) }],
      };
    });

    return (
      <View style={[styles.paginatorContainer, horizontalLtrDirectionStyle]}>
        <Animated.View style={[styles.dotsContainer, horizontalLtrDirectionStyle, containerAnimatedStyle]}>
          {dots.map((_, index) => (
            <AnimatedDot
              key={index}
              index={index}
              visibleDotsIndices={visibleDotsIndices}
              roundedPage={roundedPage}
              spacing={spacing}
              dotSize={dotSize}
              onPress={onDotPress ? () => onDotPress(index) : undefined}
              idleColor={idle_dot}
              activeColor={active_dot}
            />
          ))}
        </Animated.View>
      </View>
    );
  },
);

type AnimatedDotProps = {
  index: number;
  visibleDotsIndices: SharedValue<{
    start: number;
    end: number;
    currentIndex: number;
  }>;
  roundedPage: SharedValue<number>;
  spacing: number;
  dotSize: number;
  onPress?: () => void;
  idleColor: string;
  activeColor: string;
};

const AnimatedDot = memo(
  ({
    index,
    visibleDotsIndices,
    spacing,
    roundedPage,
    dotSize,
    onPress,
    idleColor,
    activeColor,
  }: AnimatedDotProps) => {
    const isVisible = useDerivedValue(() => {
      return index >= visibleDotsIndices.value.start && index < visibleDotsIndices.value.end;
    });

    const visibility = useDerivedValue(() => {
      const isActive = roundedPage.value === index;
      const opacity = isActive ? 1 : isVisible.value ? 0.75 : 0;

      return withTiming(opacity, {
        duration: 500,
        easing: Easing.linear,
      });
    }, [index, roundedPage]);

    const rContainerStyle = useAnimatedStyle(() => {
      const scale = withSpring(roundedPage.value === index ? 1 : 0.75, SpringConfig);
      return {
        opacity: visibility.value,
        marginRight: spacing,
        transform: [{ scale }],
      };
    });

    const rDotStyle = useAnimatedStyle(() => {
      const isActive = roundedPage.value === index;
      return {
        width: isActive ? dotSize * 2.2 : dotSize,
        height: dotSize,
        backgroundColor: isActive ? activeColor : idleColor,
      };
    });

    const dot_node = (
      <Animated.View style={rContainerStyle}>
        <Animated.View style={[styles.dot, rDotStyle]} />
      </Animated.View>
    );

    if (!onPress) {
      return dot_node;
    }

    return (
      <Pressable
        onPress={onPress}
        hitSlop={8}
        accessibilityRole="button"
        accessibilityLabel={`Page ${index + 1}`}
        accessibilityState={{ selected: false }}>
        {dot_node}
      </Pressable>
    );
  },
);

const styles = StyleSheet.create({
  dot: {
    borderRadius: 9999,
  },
  dotsContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
  },
  paginatorContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 24,
    width: '100%',
    paddingHorizontal: 24,
    overflow: 'visible',
  },
});
